import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import clsx from 'clsx';
import { type CSSProperties, useEffect, useRef, useState } from 'react';

import styles from './ImageSlider.module.scss';

interface Image {
  url: string;
  alt: string;
}

export const ImageSlider = ({
  images,
  spacer = 24,
  className,
}: {
  images: Image[];
  spacer?: number;
  className?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [current, setCurrent] = useState(0);

  const handlePrev = () => setCurrent((prev) => prev - 1);
  const handleNext = () => setCurrent((prev) => (prev + 1) % images.length);

  useEffect(() => {
    if (ref.current) {
      const slider = ref.current;
      const slides = slider.children as HTMLCollectionOf<HTMLElement>;

      const { scrollWidth, clientWidth, scrollLeft } = slider;

      const containerOffset = slides[0].offsetLeft; // the x padding offset of the slider container
      const currentSlideLeft = slides[current].offsetLeft;

      const slideLeft = currentSlideLeft - containerOffset;
      const maxScrollLeft = scrollWidth - clientWidth;

      let newScrollLeft = slideLeft;

      if (slideLeft > maxScrollLeft) {
        if (scrollLeft !== maxScrollLeft) {
          newScrollLeft = maxScrollLeft;
        } else {
          newScrollLeft = 0;
          setCurrent(0);
        }
      }

      slider.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  }, [current, spacer]);

  return (
    <div className={clsx(styles.component, className)}>
      <div
        data-ui="slider"
        ref={ref}
        style={{ '--slider-spacer': `${spacer}px` } as CSSProperties}
      >
        {images.map((image, idx) => (
          <figure key={idx}>
            <img alt={image.alt || ''} src={image.url} />
          </figure>
        ))}
      </div>
      <nav data-ui="slider-nav">
        <button
          aria-label="Previous"
          disabled={current === 0}
          onClick={handlePrev}
        >
          <ChevronLeft />
        </button>
        <button aria-label="Next" onClick={handleNext}>
          <ChevronRight />
        </button>
      </nav>
    </div>
  );
};
